import { useRef, useState } from 'react';
import { ModalCloseButton } from '@busy/ui';
import { useCutTimer } from '../../store/cutTimer';
import { useT } from '../../lib/i18n';
import { PhotoCaptureModal, type PhotoCropArea } from '../PhotoCaptureModal';
import { VideoCaptureModal } from '../VideoCaptureModal';
import { CaptureChoice } from './CaptureChoice';

// Optional capture step between picking a service and starting the timer.
// "Snap the before — turn the cut into a share." Skip is right there with
// equal weight so this never blocks a busy chair. When the barber confirms
// (or skips), we transition to 'running' which kicks off the actual cut
// session in the store.
export function BeforePhotoStep() {
  const t = useT();
  const beforePhotoUrl = useCutTimer((s) => s.beforePhotoUrl);
  const setBeforePhoto = useCutTimer((s) => s.setBeforePhoto);
  const setBeforeVideo = useCutTimer((s) => s.setBeforeVideo);
  const startCutFromBefore = useCutTimer((s) => s.startCutFromBefore);
  const backToPicker = useCutTimer((s) => s.backToPicker);
  const closeModal = useCutTimer((s) => s.closeModal);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  // High-fidelity capture: PhotoCaptureModal calls onCaptureNative
  // synchronously before onCapture in handleConfirm. We stash the
  // source + crop in refs (persist across re-renders, don't trigger
  // them) so handleCaptured can bundle everything into one
  // setBeforePhoto call — no torn state where the cropped blob lands
  // in the store before the source is wired up.
  const pendingNativeRef = useRef<{ source: Blob; crop: PhotoCropArea } | null>(null);
  function handleCapturedNative(source: Blob, crop: PhotoCropArea) {
    pendingNativeRef.current = { source, crop };
  }
  async function handleCaptured(blob: Blob) {
    const pending = pendingNativeRef.current;
    pendingNativeRef.current = null;
    setBeforePhoto(blob, pending?.source, pending?.crop);
    setBeforeVideo(null); // photo path — drop any prior recorded clip
    setCaptureOpen(false);
  }

  async function handleVideoCaptured(out: {
    photoBlob: Blob;
    videoBlob: Blob;
    videoCrop: PhotoCropArea | null;
  }) {
    setBeforePhoto(out.photoBlob);
    setBeforeVideo(out.videoBlob, out.videoCrop);
    setVideoOpen(false);
  }

  function handleSkip() {
    startCutFromBefore();
  }

  function handleStart() {
    startCutFromBefore();
  }

  return (
    <>
      <div className="bg-navy-900 text-white px-6 pt-7 pb-6 rounded-t-3xl relative">
        <ModalCloseButton onClose={closeModal} variant="dark" />
        <button
          type="button"
          onClick={backToPicker}
          className="absolute top-4 left-4 text-white/70 hover:text-white inline-flex items-center gap-1 text-xs font-bold tracking-wide uppercase px-2 py-1 rounded hover:bg-white/10"
        >
          <ChevronLeft />
          {t('cut.back')}
        </button>
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-gold-400 inline-flex items-center gap-1.5 mt-2 mb-2">
          <CameraIcon className="w-3 h-3" />
          {t('cut.before.eyebrow')}
        </div>
        <h2 className="font-display text-3xl leading-tight">{t('cut.before.title')}</h2>
        <p className="text-white/72 text-sm leading-relaxed mt-2 max-w-sm">
          {t('cut.before.body')}
        </p>
      </div>

      <div className="p-5 space-y-3">
        {beforePhotoUrl ? (
          <div className="rounded-2xl overflow-hidden border border-line bg-bg-alt">
            <img src={beforePhotoUrl} alt="" className="w-full aspect-square object-cover" />
            <div className="p-3 flex items-center gap-2">
              <span className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-500">
                {t('cut.before.captured')}
              </span>
              <button
                type="button"
                onClick={() => setCaptureOpen(true)}
                className="ml-auto h-9 px-3 rounded-lg border border-line bg-surface text-xs font-bold text-ink-700"
              >
                {t('cut.before.retake')}
              </button>
            </div>
          </div>
        ) : (
          // Equal-sized photo/video options. Same shape, same icon size,
          // same hover affordance — barber picks the format that fits the
          // moment without one feeling primary over the other.
          <div className="grid grid-cols-2 gap-2">
            <CaptureChoice
              kind="photo"
              label={t('cut.before.photoChoice')}
              onClick={() => setCaptureOpen(true)}
            />
            <CaptureChoice
              kind="video"
              label={t('cut.before.videoChoice')}
              onClick={() => setVideoOpen(true)}
            />
          </div>
        )}

        <div className="flex gap-2 pt-1">
          {beforePhotoUrl ? (
            <button
              type="button"
              onClick={handleStart}
              className="flex-1 h-12 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold inline-flex items-center justify-center gap-2"
            >
              {t('cut.before.startWithPhoto')}
              <ChevronRight />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSkip}
              className="flex-1 h-12 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold inline-flex items-center justify-center gap-2"
            >
              {t('cut.before.skipAndStart')}
              <ChevronRight />
            </button>
          )}
        </div>

        {!beforePhotoUrl && (
          <p className="text-[11px] text-ink-500 text-center leading-snug">
            {t('cut.before.skipHint')}
          </p>
        )}
      </div>

      <PhotoCaptureModal
        open={captureOpen}
        title={t('cut.before.captureTitle')}
        composeRole="before"
        onCapture={handleCaptured}
        onCaptureNative={handleCapturedNative}
        onClose={() => setCaptureOpen(false)}
      />

      <VideoCaptureModal
        open={videoOpen}
        title={t('cut.before.videoTitle')}
        composeRole="before"
        onCapture={handleVideoCaptured}
        onClose={() => setVideoOpen(false)}
      />
    </>
  );
}

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}
